import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiGet, apiDelete } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import OButton from '../../components/reusable/OButton'
import ODataTable from '../../components/reusable/ODataTable'
import OPagination from '../../components/reusable/OPagination'
import OSelect from '../../components/reusable/OSelect'
import { AiOutlineEdit } from 'react-icons/ai'
import { TiTrash } from 'react-icons/ti'
import AuthContext from 'context/AuthContext'
import helpers from 'utils/helpers'
import RecruiterModal from './RecruiterModal'

const Recruiters = () => {
  const { t } = useTranslation()
  const { user } = useContext(AuthContext)
  const permission = user?.permission?.find((p) => p.manager === 'careers')

  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editShowModal, setEditShowModal] = useState(false)
  const [item, setItem] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 0,
    totalItems: 0,
    limit: 10,
  })
  const [sort, setSort] = useState({ sortKey: 'createdAt', sortType: 'desc' })
  const [filters, setFilters] = useState({
    keyword: '',
    status: '',
  })

  const fetchRecruiters = async (opts = {}) => {
    try {
      setLoading(true)
      const page = opts.page || pagination.page
      const params = new URLSearchParams()
      params.set('page', page)
      params.set('pageSize', pagination.limit)
      if (sort?.sortKey) params.set('sortKey', sort.sortKey)
      if (sort?.sortType) params.set('sortType', sort.sortType)
      if (filters.keyword) params.set('keyword', filters.keyword)
      if (filters.status) params.set('status', filters.status)

      const res = await apiGet(`${apiPath.careerRecruiters}?${params.toString()}`)
      const results = res?.data?.results || {}
      const items = results?.docs || []
      setList(items)
      setPagination({
        page: results.page || page,
        pageCount: results.totalPages || 0,
        totalItems: results.totalDocs || 0,
        limit: results.limit || pagination.limit,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRecruiters({ page: 1 })
  }, [sort, filters.status])

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRecruiters({ page: 1 })
    }, 300)
    return () => clearTimeout(handler)
  }, [filters.keyword])

  const onPageChange = ({ selected }) => {
    fetchRecruiters({ page: selected + 1 })
  }

  const deleteRecruiter = async (id) => {
    const res = await apiDelete(`${apiPath.careerRecruiterById}/${id}`)
    if (res?.data?.success) {
      await fetchRecruiters({ page: pagination.page })
    }
  }

  const handleEdit = (row) => {
    setItem(row)
    setEditShowModal(true)
  }

  const columns = [
    { title: t('O_FULL_NAME') || 'Name', key: 'name', sortable: true },
    {
      title: t('CAREERS_APPLICATIONS') || 'Applications',
      key: 'applications',
      render: (row) =>
        Array.isArray(row.applications) ? row.applications.length : 0,
    },
    {
      title: t('O_STATUS'),
      key: 'status',
      sortable: true,
      align: 'center',
      render: (row) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      title: t('O_CREATED_AT'),
      key: 'createdAt',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      title: t('O_ACTION'),
      key: 'actions',
      align: 'center',
      render: (row) => (
        <div className='flex gap-3 justify-center'>
          {(permission?.add || user?.role === 'admin') && (
            <button
              className='hover:text-indigo-700'
              title={t('O_EDIT')}
              onClick={() => handleEdit(row)}
            >
              <AiOutlineEdit size={18} />
            </button>
          )}
          {(permission?.delete || user?.role === 'admin') && (
            <button
              className='hover:text-red-700 text-red-600'
              title={t('O_DELETE') || 'Delete'}
              onClick={() =>
                helpers.alertFunction(
                  `Are you sure want to delete ${row.name || 'this recruiter'}?`,
                  row._id,
                  deleteRecruiter,
                  true
                )
              }
            >
              <TiTrash size={18} />
            </button>
          )}
        </div>
      ),
    },
  ]

  const statusOptions = [
    { value: '', key: t('All Statuses') || 'All Statuses' },
    { value: 'active', key: t('active') || 'active' },
    { value: 'inactive', key: t('inactive') || 'inactive' },
  ]

  return (
    <div>
      <div className='bg-white'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9]'>
            <div className='border-b border-b-[#E3E3E3] flex flex-wrap items-end gap-2 px-4 py-3'>
              <div className='w-full md:w-60'>
                <label className='block mb-2 text-sm font-medium text-gray-900'>
                  {t('O_SEARCH')}
                </label>
                <input
                  className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
                  placeholder={t('SEARCH_BY_KEYWORD')}
                  value={filters.keyword}
                  onChange={(e) =>
                    setFilters({ ...filters, keyword: e.target.value })
                  }
                />
              </div>
              <div className='w-full md:w-52'>
                <OSelect
                  inputLabel={t('O_STATUS')}
                  name='status'
                  register={{
                    name: 'status',
                    onChange: (e) =>
                      setFilters({ ...filters, status: e.target.value }),
                  }}
                  errors={{}}
                  options={statusOptions}
                  value={filters.status}
                  wrapperClassName='w-full'
                />
              </div>
              {(permission?.add || user?.role === 'admin') && (
                <div className='ml-auto'>
                  <OButton
                    label={t('Add Recruiter') || 'Add Recruiter'}
                    onClick={() => setShowModal(true)}
                  />
                </div>
              )}
            </div>

            <ODataTable
              data={list}
              columns={columns}
              loading={loading}
              pagination={pagination}
              onPageChange={onPageChange}
              sort={sort}
              onSortChange={setSort}
            />

            <OPagination
              pagination={pagination}
              onPageChange={onPageChange}
              page={pagination.page}
            />
          </div>
        </div>
      </div>

      {showModal ? (
        <RecruiterModal
          setShowModal={setShowModal}
          fetchRecruiters={() => fetchRecruiters({ page: pagination.page })}
        />
      ) : null}

      {editShowModal ? (
        <RecruiterModal
          setShowModal={setEditShowModal}
          fetchRecruiters={() => fetchRecruiters({ page: pagination.page })}
          item={item}
        />
      ) : null}
    </div>
  )
}

export default Recruiters
